/**
 * LINKEDIN POST FORM MODAL — Modale per registrare un post/contatto LinkedIn,
 * aperta dall'azione rapida "LinkedIn" su PersonDetail/BusinessDetail.
 *
 * Entità reale `LinkedinPost` (gemella di Call/Email/Note). Il "contattato" NON è
 * un campo: è implicito tramite le associazioni (Relationship polimorfica creata
 * al submit via useAssociatedRecordsBinder).
 *
 * Campi:
 *  - Data attività (datetime-local, default ora) -> activityDate ISO
 *  - Messaggio (WYSIWYG RichTextEditor) -> message HTML serializzato
 *  - Associa ai record (AssociatedRecordsField)
 *
 * Il blocco "follow-up" (checkbox + 2 tendine) è mantenuto nel codice ma NON
 * renderizzato (SHOW_FOLLOW_UP = false): diventerà un componente condiviso a più
 * entità in un task successivo.
 */

import { useRef, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControlLabel from '@mui/material/FormControlLabel';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import TextField from '@mui/material/TextField';
import CloseIcon from '@mui/icons-material/Close';
import { datetimeLocalToIso, nowDatetimeLocal } from '@crm/shared';
import { RichTextEditor } from '../../components/RichTextEditor';
import { useAssociatedRecordsBinder } from '../../components/useAssociatedRecordsBinder';
import { AssociatedRecordsField } from '../../components/AssociatedRecordsField';
import { useToast } from '../../components/useToast';
import { useAppDispatch } from '../../store/hooks';
import { getEntityFqn } from '../../entityRegistry/entityRegistry';
import { createLinkedinPost } from './linkedinPostThunks';
import type { LinkedinPost, LinkedinPostCreatePayload } from './linkedinPostTypes';

interface LinkedinPostFormModalProps {
  onClose: () => void;
  onCreated?: () => void;
  /** Record da pre-associare (es. la Person/Business corrente): al submit crea la
   *  Relationship bidirezionale LinkedinPost<->entità. */
  linkedEntity?: { className: string; classPK: number };
}

const FORM_ID = 'linkedin-post-form';

// Il blocco follow-up resta nel codice ma non viene renderizzato: sarà un
// componente condiviso a più entità in un task successivo.
const SHOW_FOLLOW_UP = false;

/** true se l'HTML contiene testo reale (ignora tag e spazi). */
function htmlHasText(html: string): boolean {
  return html.replace(/<[^>]*>/g, '').replace(/\u00A0/g, ' ').trim().length > 0;
}

export default function LinkedinPostFormModal({
  onClose,
  onCreated,
  linkedEntity,
}: LinkedinPostFormModalProps) {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const toast = useToast();

  const [submitting, setSubmitting] = useState(false);
  const [activityDateLocal, setActivityDateLocal] = useState<string>(() => nowDatetimeLocal());
  // Messaggio WYSIWYG: il RichTextEditor è non controllato, emette l'HTML via onChange.
  const [message, setMessage] = useState('');
  // Follow-up (placeholder, non renderizzato — vedi SHOW_FOLLOW_UP).
  const [createFollowUp, setCreateFollowUp] = useState(false);
  const [followUpKind, setFollowUpKind] = useState('todo');
  const [followUpWhen, setFollowUpWhen] = useState('mon3wd');
  // Rimonta il RichTextEditor (non controllato) per svuotarlo dopo un submit "keep".
  const [editorKey, setEditorKey] = useState(0);

  const linkedinPostFqn = getEntityFqn('LinkedinPost');

  const { records, setRecords, applyAtSubmit, pickerProps } = useAssociatedRecordsBinder({
    mode: 'create',
    entityFqn: linkedinPostFqn ?? undefined,
    reference: linkedEntity ?? null,
  });

  const submitIntentRef = useRef<'close' | 'keep'>('close');

  const canSubmit = activityDateLocal.length > 0 && htmlHasText(message);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!canSubmit) return;
    const intent = submitIntentRef.current;
    submitIntentRef.current = 'close';

    const payload: LinkedinPostCreatePayload = {
      activityDate: datetimeLocalToIso(activityDateLocal) ?? '',
      message,
    };

    setSubmitting(true);
    try {
      let created: LinkedinPost;
      try {
        created = (await dispatch(createLinkedinPost(payload)).unwrap()) as LinkedinPost;
      } catch (err) {
        toast({ severity: 'error', message: err instanceof Error ? err.message : String(err) });
        return;
      }
      if (!linkedinPostFqn) {
        console.warn(
          '[relationship] LinkedinPostFormModal: LinkedinPost non registrato nel registry — link saltati',
          { selectedRecords: records.length },
        );
      } else {
        await applyAtSubmit?.(linkedinPostFqn, created.linkedinPostId);
      }
      toast({ severity: 'success', message: t('common.saved') });
      onCreated?.();
      if (intent === 'keep') {
        setActivityDateLocal(nowDatetimeLocal());
        setMessage('');
        setEditorKey((k) => k + 1);
        setRecords?.([]);
      } else {
        onClose();
      }
    } finally {
      setSubmitting(false);
    }
  }

  const footer = (
    <>
      <Button
        type="submit"
        form={FORM_ID}
        onClick={() => {
          submitIntentRef.current = 'close';
        }}
        variant="contained"
        disabled={submitting || !canSubmit}
      >
        {t('common.create')}
      </Button>
      <Button type="button" onClick={onClose} variant="outlined">
        {t('common.cancel')}
      </Button>
    </>
  );

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth className="app-dialog">
      <DialogTitle className="app-dialog__header">
        <span className="app-dialog__title">{t('linkedin.createTitle')}</span>
        <IconButton
          aria-label={t('common.cancel')}
          onClick={onClose}
          size="small"
          className="app-dialog__close"
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent className="app-dialog__body">
        <form id={FORM_ID} onSubmit={handleSubmit} className="linkedin-form">
          {/* Data attività */}
          <div className="form-field">
            <TextField
              type="datetime-local"
              label={t('linkedin.activityDate')}
              value={activityDateLocal}
              onChange={(e) => setActivityDateLocal(e.target.value)}
              required
              fullWidth
              slotProps={{ inputLabel: { shrink: true } }}
            />
          </div>

          {/* Messaggio WYSIWYG */}
          <div className="form-field">
            <label className="field-row__label text-xs text-muted">{t('linkedin.message')}</label>
            <RichTextEditor
              key={editorKey}
              onChange={setMessage}
              placeholder={t('linkedin.messagePlaceholder')}
            />
          </div>

          {/* Associa ai record (polimorfico) */}
          <AssociatedRecordsField
            {...pickerProps}
            records={records}
            onChange={(val) => setRecords?.(val)}
            displayMode="click-to-edit"
          />

          {/* Follow-up: mantenuto nel codice ma non renderizzato (SHOW_FOLLOW_UP). */}
          {SHOW_FOLLOW_UP && (
            <div className="linkedin-form__followup">
              <FormControlLabel
                control={
                  <Checkbox
                    checked={createFollowUp}
                    onChange={(e) => setCreateFollowUp(e.target.checked)}
                  />
                }
                label={t('linkedin.followUp.prefix')}
              />
              <Select
                size="small"
                value={followUpKind}
                onChange={(e) => setFollowUpKind(e.target.value)}
                disabled={!createFollowUp}
                className="linkedin-form__followup-select"
              >
                <MenuItem value="todo">{t('linkedin.followUp.kindTodo')}</MenuItem>
              </Select>
              <span className="linkedin-form__followup-text">{t('linkedin.followUp.middle')}</span>
              <Select
                size="small"
                value={followUpWhen}
                onChange={(e) => setFollowUpWhen(e.target.value)}
                disabled={!createFollowUp}
                className="linkedin-form__followup-select"
              >
                <MenuItem value="mon3wd">{t('linkedin.followUp.when3wd')}</MenuItem>
              </Select>
            </div>
          )}
        </form>
      </DialogContent>
      <DialogActions className="app-dialog__footer">{footer}</DialogActions>
    </Dialog>
  );
}
